import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { DateRangePicker } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import "../../react-dates-custom.css";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  IconButton,
  Chip,
  Checkbox,
  Tooltip,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Popper from "@material-ui/core/Popper";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import LayersIcon from "@material-ui/icons/Layers";
import FilterListIcon from "@material-ui/icons/FilterList";
import ViewListIcon from "@material-ui/icons/ViewList";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PhoneCallbackIcon from "@material-ui/icons/PhoneCallback";
import RemoveShoppingCartIcon from "@material-ui/icons/RemoveShoppingCart";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import InsightsIcon from "@material-ui/icons/Timeline";
import GridOnIcon from "@material-ui/icons/GridOn";
import DateRangeIcon from "@material-ui/icons/DateRange";
import InboxIcon from "@material-ui/icons/MoveToInbox";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { URL } from "../../config/config";
import {
  day,
  fmt,
  istDaysAgo,
  istToday,
  daysForSlugs,
  sumMetrics,
  ANALYTICS_RANGES,
  fillDayGaps,
} from "./analyticsUtils";
import {
  CampaignTrend,
  DayWiseTable,
  MonthCalendar,
} from "./AnalyticsViews";
import HourlyComparison from "./HourlyComparison";

/**
 * Campaign Leads — leads and conversions per checkout page.
 *
 * Data comes from whatsapp-rest-api's superadmin endpoints
 * (/superadmin/checkout-analytics-pages, /superadmin/checkout-analytics-leads);
 * both are gated server-side on the "checkout-analytics" ACL permission, and
 * this route stays
 * hidden until a role grants it in Manage ▸ Roles.
 *
 * `subSource` (the readable page name) rides on each row straight from the
 * customer docs — integrations-server stamps it at creation from region
 * config. Rows without one fall back to their region/version slug; several
 * slugs may share one name and roll up into a single row here.
 *
 * Sub-source and slug are two ways of naming the same page, so they filter
 * as an OR rather than a forced chain.
 * Stage semantics (server-derived): PAID > CALLBACK > ABANDONED > LEAD.
 */

const PAGE_SIZE = 25;

/* Brand green, with neutrals carrying a faint green cast so the page reads
   as AiSensy. Green is spent on data and affordances, never on chrome. */
const G = "#04b85c";
const G_SOFT = "#e9f9f0";
const INK = "#0e1f18";
const BODY = "#3c4a43";
const MUTED = "#7b8a83";
const LINE = "#e6ebe8";

const STAGE = {
  LEAD: { fg: "#5a6472", bg: "#f0f2f1", label: "Lead" },
  CALLBACK: { fg: "#1565c0", bg: "#e7f0fa", label: "Callback" },
  ABANDONED: { fg: "#b95000", bg: "#fdf1e7", label: "Abandoned" },
  PAID: { fg: "#0f7a3d", bg: "#e6f6ec", label: "Paid" },
};
const STAGE_OPTIONS = [
  { value: "", label: "All stages" },
  ...["LEAD", "CALLBACK", "ABANDONED", "PAID"].map((s) => ({
    value: s,
    label: STAGE[s].label,
  })),
];
// The UI shows two buckets, not three: regional plans and offers are the same
// kind of self-serve checkout page and are read as one. The split still exists
// in the data, so each bucket carries the raw sources it covers — the filter
// sends them as a comma list, and a row's raw source resolves back through the
// same map so the table never shows a name the filter can't offer.
const SOURCES = [
  { value: "", label: "All sources", members: [] },
  {
    value: "offers",
    label: "Offers",
    members: ["aisensy_regional_plans", "aisensy_offers"],
  },
  { value: "webinar", label: "Webinar", members: ["aisensy_webinar"] },
];
// One list drives both the header cells and the comparator, so a column can
// never offer a sort it doesn't actually apply. `value` is what gets compared.
const COLUMNS = [
  {
    key: "name",
    label: "Sub-source",
    align: "left",
    numeric: false,
    value: (x) => x.name.toLowerCase(),
  },
  {
    key: "uniqueVisitors",
    label: "Visits",
    align: "right",
    numeric: true,
    value: (x) => x.uniqueVisitors || 0,
  },
  { key: "leads", label: "Leads", align: "right", numeric: true, value: (x) => x.leads },
  {
    key: "callback",
    label: "Callback",
    align: "right",
    numeric: true,
    value: (x) => x.callback,
  },
  {
    key: "abandoned",
    label: "Abandoned",
    align: "right",
    numeric: true,
    value: (x) => x.abandoned,
  },
  { key: "paid", label: "Paid", align: "right", numeric: true, value: (x) => x.paid },
  {
    key: "conv",
    label: "Lead \u2192 paid",
    align: "right",
    numeric: true,
    value: (x) => (x.leads ? (x.paid * 100) / x.leads : 0),
  },
  {
    key: "lastLeadAt",
    label: "Last lead",
    align: "right",
    numeric: true,
    value: (x) => x.lastLeadAt || "",
  },
];
// Page names run far longer than the input they sit under ("Interakt
// Competitor Ads — Google (Basic+Pro)"), and MUI pins the popup to the anchor
// width by default, which truncates every one of them. Let the list size to
// its content instead, floored at the input width.
const WidePopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ width: "auto", minWidth: (props.style || {}).width }}
  />
);

// Visit tracking went live on this date. Leads predate it by years, so the
// column is explained inline rather than leaving a reader to wonder why a
// busy page shows no visits.
const VISITS_SINCE = "31 Jul 2026";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", AED: "AED " };
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2.5, 3, 3),
    backgroundColor: "#f5f8f6",
    // ProjectsLayout's route container is height:100vh + overflow:hidden —
    // this page must own its scrolling or everything below the fold, the
    // pager included, gets clipped.
    height: "100vh",
    overflowY: "auto",
    width: "100%",
    boxSizing: "border-box",
    color: BODY,
  },
  content: { maxWidth: 1720, margin: "0 auto" },

  /* Entering the analytics view is a mode change, not a tab change — the
     motion is what tells a reader the whole surface swapped. */
  "@keyframes riseIn": {
    from: { opacity: 0, transform: "translateY(14px) scale(.995)" },
    to: { opacity: 1, transform: "none" },
  },
  "@keyframes sweepIn": {
    from: { opacity: 0, transform: "translateX(-10px)" },
    to: { opacity: 1, transform: "none" },
  },
  "@keyframes pulseRing": {
    "0%": { boxShadow: "0 0 0 0 rgba(4,184,92,.34)" },
    "70%": { boxShadow: "0 0 0 9px rgba(4,184,92,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(4,184,92,0)" },
  },
  "@keyframes popIn": {
    from: { opacity: 0, transform: "translateY(16px) scale(.97)" },
    to: { opacity: 1, transform: "none" },
  },
  "@keyframes labelSwap": {
    from: { opacity: 0, transform: "translateX(6px)" },
    to: { opacity: 1, transform: "none" },
  },
  rise: { animation: "$riseIn .34s cubic-bezier(.22,.85,.3,1) both" },
  riseDelay: { animationDelay: ".08s" },
  sweep: { animation: "$sweepIn .3s cubic-bezier(.22,.85,.3,1) both" },
  /* Staged entrance: tiles, then chart, then calendar, then table. Each piece
     arrives as the eye reaches it, which reads as the view assembling rather
     than as one block appearing. Total under half a second. */
  pop: { animation: "$popIn .42s cubic-bezier(.2,.8,.25,1) both" },
  /* Keyed on the tab, so the label re-mounts and slides in on every switch
     instead of swapping text in place. */
  modeBtnInner: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    animation: "$labelSwap .26s cubic-bezier(.2,.8,.25,1) both",
  },

  /* The mode switch. Top-right, unmissable, and it reads as an action
     ("switch to…") rather than as one more tab in a row of tabs. */
  modeBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    cursor: "pointer",
    borderRadius: 999,
    padding: "9px 18px",
    fontSize: 13.5,
    fontWeight: 750,
    fontFamily: "inherit",
    letterSpacing: 0.1,
    color: "#fff",
    background: "linear-gradient(135deg, #04b85c 0%, #039e50 100%)",
    boxShadow: "0 6px 18px rgba(4,184,92,.28)",
    transition: "transform .18s cubic-bezier(.22,.85,.3,1), box-shadow .18s ease",
    animation: "$pulseRing 2.6s ease-out 3",
    "&:hover": {
      transform: "translateY(-1.5px)",
      boxShadow: "0 10px 24px rgba(4,184,92,.36)",
    },
    "&:active": { transform: "translateY(0)" },
  },
  modeBtnBack: {
    color: "#0f7a3d",
    background: "#fff",
    border: `1px solid #cfeedd`,
    boxShadow: "0 2px 8px rgba(14,31,24,.06)",
    animation: "none",
    "&:hover": { boxShadow: "0 6px 16px rgba(14,31,24,.10)" },
  },
  modeIcon: { fontSize: 18, transition: "transform .25s ease" },

  /* Range chips for the analytics view. */
  rangeBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "0 16px 14px",
  },
  rangeChip: {
    cursor: "pointer",
    border: `1px solid ${LINE}`,
    background: "#f4f7f5",
    color: MUTED,
    borderRadius: 999,
    padding: "5px 13px",
    fontSize: 12.5,
    fontWeight: 700,
    fontFamily: "inherit",
    transition: "all .16s ease",
    "&:hover": { borderColor: "#bfe6d1", color: "#0f7a3d" },
  },
  dateInput: {
    border: `1px solid ${LINE}`,
    borderRadius: 999,
    background: "#fff",
    color: INK,
    padding: "5px 12px",
    fontSize: 12.5,
    fontWeight: 650,
    fontFamily: "inherit",
    outline: "none",
    "&:focus": { borderColor: G, boxShadow: "0 0 0 3px rgba(4,184,92,.14)" },
  },
  rangeChipOn: {
    background: G_SOFT,
    color: "#0f7a3d",
    borderColor: "#9fdcbb",
    boxShadow: "inset 0 0 0 1px #9fdcbb",
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  refreshBtn: {
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    backgroundColor: "#fff",
    color: "#0f7a3d",
    padding: 9,
    "&:hover": { backgroundColor: "#f2fbf6", borderColor: G },
  },
  title: { fontWeight: 700, color: INK, letterSpacing: -0.3, fontSize: 21 },
  subtitle: { color: MUTED, marginTop: 2, fontSize: 13 },

  card: {
    borderRadius: 14,
    border: `1px solid ${LINE}`,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    backgroundColor: "#fff",
  },
  section: { padding: theme.spacing(1.75, 2.5), marginBottom: theme.spacing(1.5) },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 15,
    fontWeight: 700,
    color: INK,
    marginBottom: theme.spacing(2),
  },

  /**
   * Funnel ribbon — one compact strip instead of five tall cards. Reads
   * left→right as the actual pipeline, each segment carrying its stage
   * colour, and buys back ~110px of vertical space so the table starts near
   * the top of the viewport instead of below the fold.
   */
  ribbon: {
    display: "flex",
    alignItems: "stretch",
    flexWrap: "wrap",
    borderRadius: 14,
    border: `1px solid ${LINE}`,
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    overflow: "hidden",
    marginBottom: theme.spacing(1.5),
  },
  seg: {
    flex: "1 1 150px",
    display: "flex",
    alignItems: "center",
    gap: 13,
    padding: "18px 20px",
    borderRight: `1px solid ${LINE}`,
    position: "relative",
    "&:last-child": { borderRight: "none" },
  },
  segIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  segValue: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.1,
    color: INK,
    fontVariantNumeric: "tabular-nums",
  },
  segLabel: { fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, marginTop: 2 },
  segOut: { marginLeft: "auto", background: "#fbfdfc" },

  /* Labelled fields on a grid, so nothing reflows into a jumble when one
     control grows. */
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#66736c",
    display: "block",
    marginBottom: 4,
  },
  field: {
    "&.MuiTextField-root": {
      backgroundColor: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: 8,
      transition: "border-color .15s, box-shadow .15s",
    },
    "&.MuiTextField-root:hover": { borderColor: "#c6d3cc" },
    "&.MuiTextField-root:focus-within": {
      borderColor: G,
      boxShadow: `0 0 0 3px ${G}22`,
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": {
      display: "none",
    },
    "& .MuiInputBase-root": { padding: "0 8px", minHeight: 34 },
    "& .MuiInputBase-input": { fontSize: 12.5, color: BODY },
  },
  /* the OR between the two ways of naming a page */
  resetIcon: {
    border: `1px solid ${LINE}`,
    borderRadius: 8,
    backgroundColor: "#fff",
    color: G,
    width: 34,
    height: 34,
    "&:hover": { borderColor: G, backgroundColor: "#f6fdf9" },
    "&.Mui-disabled": { color: "#c3cec8", borderColor: LINE },
  },
  // react-dates ships its own input chrome; pull it back to the height and
  // border of the fields beside it so the row reads as one control strip.
  // react-dates is styled app-wide by react-dates-custom.css (two bordered
  // boxes, each with a calendar glyph). Only nudge what that file leaves free.
  rangeWrap: {
    "& .DateInput": { height: 34 },
    "& .DateRangePicker, & .DateRangePickerInput": { display: "flex" },
  },
  optionPaper: {
    "& .MuiAutocomplete-option": {
      fontSize: 13,
      whiteSpace: "nowrap",
      paddingTop: 5,
      paddingBottom: 5,
    },
    // Long enough for the widest page name, short enough not to cover the row.
    "& .MuiAutocomplete-listbox": { maxWidth: 520 },
  },
  rangeArrow: { color: MUTED, fontSize: 13 },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: G_SOFT,
    color: "#0f7a3d",
    fontWeight: 600,
    border: "1px solid #cfeedd",
  },

  resultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 22px",
    borderBottom: `1px solid ${LINE}`,
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  countBadge: {
    backgroundColor: G_SOFT,
    color: "#0f7a3d",
    borderRadius: 20,
    padding: "1px 11px",
    fontSize: 13,
    fontWeight: 700,
    marginLeft: 4,
  },
  pager: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  /* Four mutually-exclusive stages read better as one visible row of pills
     than as a dropdown that hides three of them behind a click. */
  stagePills: { display: "flex", gap: 6, flexWrap: "wrap" },
  stagePill: {
    font: "inherit",
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 13px",
    borderRadius: 999,
    cursor: "pointer",
    backgroundColor: "#fff",
    color: MUTED,
    border: `1px solid ${LINE}`,
    transition: "background .12s, color .12s, border-color .12s",
    "&:hover": { borderColor: "#c6d3cc", color: BODY },
  },
  pagerBtn: {
    border: `1px solid ${LINE}`,
    borderRadius: 8,
    padding: 5,
    color: "#4b5563",
    "&:hover": { backgroundColor: "#f4f8f6" },
  },
  pagePill: {
    minWidth: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${G}`,
    color: "#0f7a3d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },

  tableWrap: { overflowX: "auto" },
  headCell: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    backgroundColor: "#fafcfb",
    color: MUTED,
    fontSize: 12,
    borderBottom: `1px solid ${LINE}`,
    letterSpacing: "0.02em",
  },
  sortLabel: {
    "&:hover, &:focus": { color: INK },
    "&.MuiTableSortLabel-active": { color: INK },
    "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": { color: G },
  },
  headInfo: {
    fontSize: 13,
    marginLeft: 4,
    verticalAlign: "-2px",
    color: "#9aa8a1",
    cursor: "help",
  },
  cell: { fontSize: 13, color: BODY, borderBottom: "1px solid #f1f4f2" },
  num: {
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  clickRow: {
    cursor: "pointer",
    "&:hover": { backgroundColor: "#f6fbf8" },
    "&:hover $nameText": { color: "#0f7a3d" },
  },
  nameText: { fontWeight: 600, fontSize: 13.5, color: INK },
  slugText: { fontSize: 11.5, color: MUTED, marginTop: 3, fontFamily: MONO },
  srcTag: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#0f7a3d",
    backgroundColor: G_SOFT,
    border: "1px solid #cfeedd",
    borderRadius: 5,
    padding: "2px 7px",
    marginBottom: 5,
  },
  convCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "flex-end",
  },
  convTrack: {
    width: 58,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#eaefec",
    overflow: "hidden",
    flexShrink: 0,
  },
  convFill: {
    display: "block",
    height: "100%",
    borderRadius: 3,
    backgroundColor: G,
  },
  convPct: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    minWidth: 46,
    textAlign: "right",
  },
  mono: {
    fontFamily: MONO,
    fontSize: 12,
    color: MUTED,
    maxWidth: 150,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "inline-block",
  },
  mut: { color: MUTED },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: "#0f7a3d",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    marginBottom: theme.spacing(1.5),
    "&:hover": { textDecoration: "underline" },
  },
  empty: { padding: theme.spacing(8), textAlign: "center", color: "#9aa8a1" },
  emptyTitle: { fontSize: 14.5, fontWeight: 600, color: INK, marginTop: 10 },
  emptyBody: { fontSize: 13, color: MUTED, marginTop: 4 },
}));

// Amounts are not all INR: uk/*, blackfriday/* and the UAE pages bill in
// USD/AED. Render what the doc says rather than assuming rupees.
const money = (amount, currency) => {
  if (!amount) return "—";
  const sym = CURRENCY_SYMBOL[currency] || (currency ? `${currency} ` : "");
  return `${sym}${fmt(amount)}`;
};
const asSlugs = (pages) =>
  (pages || []).map((p) => ({ region: p.region, version: p.version }));

// Dates start EMPTY on purpose: the page opens on all-time data and only
// narrows once the user picks a range.

export default function CheckoutAnalyticsPage() {
  const classes = useStyles();
  const [dates, setDates] = useState({ startDate: null, endDate: null });
  // Inside a selected campaign: its people (the working list) vs its
  // numbers (the report). Analytics exists ONLY per campaign — an
  // all-campaigns report answers a question nobody was asking.
  const [campaignTab, setCampaignTab] = useState("leads");
  const [focusedInput, setFocusedInput] = useState(null);
  // The picker speaks moments; the API speaks YYYY-MM-DD. Derive rather than
  // hold both, so the two can't drift apart.
  const from = dates.startDate ? dates.startDate.format("YYYY-MM-DD") : "";
  const to = dates.endDate ? dates.endDate.format("YYYY-MM-DD") : "";
  const [refreshKey, setRefreshKey] = useState(0);
  const [source, setSource] = useState("");
  const [subSourceF, setSubSourceF] = useState(null);
  // UTM Campaign filter — server-side contains match on the ad campaign the
  // buyer arrived from (utmParams.utm_campaign on the customer docs). Typed
  // value applies on Enter so we don't refetch per keystroke.
  const [utmCampaignInput, setUtmCampaignInput] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  // The analytics view owns its OWN date range, independent of the campaign
  // list's filters: it is a report, and a report that silently inherits
  // whatever the list was filtered to is a report nobody can trust.
  // Defaults to all time — the founder's first question is "how are we
  // doing", not "how were we doing last week".
  const [rangeKey, setRangeKey] = useState("all");
  const [aDates, setADates] = useState({ startDate: "", endDate: "" });
  // Day-end report: one row per IST day, expandable into its checkout pages.
  const [daily, setDaily] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [slugF, setSlugF] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("leads");
  const [sortDir, setSortDir] = useState("desc");
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(null);
  const [stage, setStage] = useState("");
  const [leads, setLeads] = useState({ leads: [], total: 0 });
  const [page, setPage] = useState(0);
  const [leadsLoading, setLeadsLoading] = useState(false);

  // --- Saved filter groups (per-founder). A group is a named set of checkout
  // pages; picking one scopes the whole screen to those pages, read either
  // Combined (the group as one page) or By page. Groups persist server-side on
  // the founder's own agent doc; which one is open and which mode is a
  // per-browser convenience kept in localStorage. ---
  const readLS = (k, d) => {
    try {
      return localStorage.getItem(k) || d;
    } catch (e) {
      return d;
    }
  };
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(() => readLS("ca_activeGroupId", null));
  const [groupMode, setGroupMode] = useState(() => readLS("ca_groupMode", "combined"));
  // Sales/Marketing channel toggle (INT-56). Which universe of checkout pages
  // the whole dashboard shows: "marketing" (default), "sales" (the /sales/
  // mirror pages) or "all". Sent to every analytics endpoint as ?channel and
  // remembered per browser like groupMode.
  const [channel, setChannel] = useState(() => readLS("ca_channel", "marketing"));
  // null, or { id?, name, keys:Set<"region|version"> } while building/editing.
  const [groupEditor, setGroupEditor] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  // Analytics window. An empty from/to is all time — the aggregation runs
  // over the whole collection in well under a second, so there is no reason
  // to make the founder pick a range before seeing anything.
  const activeRange = ANALYTICS_RANGES.find((r) => r.key === rangeKey);
  const isCustom = rangeKey === "custom";
  const aFrom = isCustom
    ? aDates.startDate || ""
    : activeRange && activeRange.days
    ? istDaysAgo(activeRange.days - 1).format("YYYY-MM-DD")
    : "";
  const aTo = isCustom
    ? aDates.endDate || ""
    : activeRange && activeRange.days
    ? istToday()
    : "";
  // Custom with only one end chosen: the fetch is deliberately skipped, so
  // `daily` still holds the PREVIOUS window. Showing it would be a lie —
  // the report renders a prompt instead.
  const customIncomplete = isCustom && !(aFrom && aTo);
  const rangeLabel = isCustom ? "Custom" : (activeRange || {}).label;

  useEffect(() => {
    // The day report only feeds a campaign's analytics view — don't spend a
    // query on it while the user is working the lead list.
    // A group's Combined view shows analytics AND leads at once, so its daily
    // series must load regardless of the (leads-defaulted) campaignTab.
    if (!(selected && (selected.isGroup || campaignTab === "analytics"))) return;
    // A half-picked custom range would query an open-ended window; wait for
    // both ends rather than flashing all-time data at the user.
    if (isCustom && !(aFrom && aTo)) return;
    setDailyLoading(true);
    const params = new URLSearchParams();
    if (aFrom) params.set("from", aFrom);
    if (aTo) params.set("to", aTo);
    params.set("channel", channel);
    axios
      .get(`${URL}/superadmin/checkout-analytics-daily?${params}`)
      .then((r) => setDaily(r.data.days || []))
      .catch(() => setDaily([]))
      .finally(() => setDailyLoading(false));
  }, [aFrom, aTo, isCustom, refreshKey, selected, campaignTab, channel]);

  // Calendar gaps become real (empty) rows before anything renders, so a
  // "vs previous day" delta is always against the actual previous day.
  const dailyDays = useMemo(
    () => fillDayGaps(daily, { through: aTo || istToday() }),
    [daily, aTo]
  );

  // One campaign's own daily series, derived from the SAME payload the
  // day-wise table renders — so a campaign's Analytics view can never
  // disagree with the all-campaigns view.
  const selectedDays = useMemo(
    () =>
      selected
        ? daysForSlugs(dailyDays, selected.slugs, {
            // Only "all time" trims to the campaign's own span; an explicit
            // range must keep every day the user asked for, zeroes included.
            trim: rangeKey === "all",
          })
        : [],
    [dailyDays, selected, rangeKey]
  );

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    // Send every raw source in the picked bucket; the API $in's a comma list.
    const members = (SOURCES.find((x) => x.value === source) || {}).members;
    if (members && members.length) params.set("source", members.join(","));
    if (utmCampaign) params.set("utmCampaign", utmCampaign);
    params.set("channel", channel);
    axios
      .get(`${URL}/superadmin/checkout-analytics-pages?${params}`)
      .then((r) => setStats(r.data.data || []))
      .catch(() => setStats([]))
      .finally(() => setLoading(false));
  }, [from, to, source, utmCampaign, refreshKey, channel]);

  // The founder's saved groups. Reloaded on manual refresh too.
  useEffect(() => {
    axios
      .get(`${URL}/superadmin/checkout-analytics-groups`)
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => setGroups([]));
  }, [refreshKey]);

  // Remember the open group / mode per browser (the groups themselves live
  // server-side; this is only "reopen where I left off").
  useEffect(() => {
    try {
      if (activeGroupId) localStorage.setItem("ca_activeGroupId", activeGroupId);
      else localStorage.removeItem("ca_activeGroupId");
    } catch (e) {}
  }, [activeGroupId]);
  useEffect(() => {
    try {
      localStorage.setItem("ca_groupMode", groupMode);
    } catch (e) {}
  }, [groupMode]);
  useEffect(() => {
    try {
      localStorage.setItem("ca_channel", channel);
    } catch (e) {}
  }, [channel]);

  const activeGroup = useMemo(
    () => groups.find((g) => g.id === activeGroupId) || null,
    [groups, activeGroupId]
  );

  // A restored group id that no longer exists (deleted elsewhere) must not
  // leave the screen scoped to nothing.
  useEffect(() => {
    if (activeGroupId && groups.length && !activeGroup) {
      setActiveGroupId(null);
      setSelected(null);
    }
  }, [groups, activeGroup, activeGroupId]);

  const groupKeySet = useMemo(
    () =>
      activeGroup
        ? new Set((activeGroup.pages || []).map((p) => `${p.region}|${p.version}`))
        : null,
    [activeGroup]
  );

  // Rows the list/table works on: every page while building a group (so any
  // page is pickable), otherwise scoped to the active group, otherwise all.
  const scopedStats = useMemo(() => {
    if (!groupKeySet) return stats;
    return stats.filter((r) => groupKeySet.has(`${r.region}|${r.version}`));
  }, [stats, groupKeySet]);

  // Every checkout page (unscoped), grouped by its readable name, for the
  // New/Edit-group dialog's picker. Independent of the scoped table above.
  const pickerPages = useMemo(() => {
    const g = {};
    for (const r of stats) {
      const key = r.subSource || `${r.region}/${r.version}`;
      const x =
        g[key] ||
        (g[key] = { name: key, source: r.source, slugs: [], leads: 0 });
      x.slugs.push({ region: r.region, version: r.version });
      x.leads += r.leads || 0;
    }
    return Object.values(g).sort((a, b) => b.leads - a.leads);
  }, [stats]);

  // The active group presented as a synthetic "page" so the existing drill-in
  // (ribbon + trend + merged leads) renders it with no new view. slugs are the
  // group's pages; metrics come from the scoped totals, not this object.
  const groupSelected = useMemo(
    () =>
      activeGroup
        ? {
            isGroup: true,
            name: activeGroup.name,
            source: null,
            slugs: (activeGroup.pages || []).map((p) => ({
              region: p.region,
              version: p.version,
            })),
          }
        : null,
    [activeGroup]
  );

  const nameOf = (r) => r.subSource || `${r.region}/${r.version}`;
  const slugOf = (r) => `${r.region}/${r.version}`;

  const subSourceOptions = useMemo(
    () => [...new Set(scopedStats.map(nameOf))].sort(),
    [scopedStats]
  );
  const slugOptions = useMemo(
    () => [...new Set(scopedStats.map(slugOf))].sort(),
    [scopedStats]
  );

  const grouped = useMemo(() => {
    const g = {};
    // Sub-source and slug name the same page two ways, so they match as an
    // OR: either one narrows, and picking both shows pages satisfying either.
    const visible = scopedStats.filter((r) => {
      if (!subSourceF && !slugF) return true;
      return (
        (subSourceF && nameOf(r) === subSourceF) ||
        (slugF && slugOf(r) === slugF)
      );
    });
    for (const r of visible) {
      const key = nameOf(r);
      const x =
        g[key] ||
        (g[key] = {
          name: key,
          source: r.source,
          slugs: [],
          uniqueVisitors: 0,
          leads: 0,
          callback: 0,
          abandoned: 0,
          paid: 0,
          lastLeadAt: "",
        });
      x.slugs.push({ region: r.region, version: r.version });
      // Uniques are NOT exactly summable across slugs — one person may appear
      // under two versions of the same page — so this is an upper bound. The
      // per-slug number is exact.
      x.uniqueVisitors += r.uniqueVisitors || 0;
      x.leads += r.leads;
      x.callback += r.callback;
      x.abandoned += r.abandoned;
      x.paid += r.paid;
      if (r.lastLeadAt > x.lastLeadAt) x.lastLeadAt = r.lastLeadAt;
    }
    const q = search.trim().toLowerCase();
    const col = COLUMNS.find((c) => c.key === sortBy) || COLUMNS[1];
    const dir = sortDir === "asc" ? 1 : -1;
    return Object.values(g)
      .filter(
        (x) =>
          !q ||
          x.name.toLowerCase().includes(q) ||
          x.slugs.some((s) => `${s.region}/${s.version}`.includes(q))
      )
      .sort((a, b) => {
        const av = col.value(a);
        const bv = col.value(b);
        if (av !== bv) return av < bv ? -dir : dir;
        // The long tail is full of ties (dozens of pages sit at 2-3 leads).
        // Without a fixed tiebreaker those rows reshuffle on every refetch,
        // so fall back to recency, then name — both total orders.
        if (a.lastLeadAt !== b.lastLeadAt) return a.lastLeadAt < b.lastLeadAt ? 1 : -1;
        return a.name < b.name ? -1 : 1;
      });
  }, [scopedStats, search, subSourceF, slugF, sortBy, sortDir]);

  const handleSort = (c) => {
    if (sortBy === c.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(c.key);
    // Counts are read biggest-first; a name is read A-Z.
    setSortDir(c.numeric ? "desc" : "asc");
  };

  const totals = useMemo(
    () =>
      grouped.reduce(
        (t, x) => ({
          uniqueVisitors: t.uniqueVisitors + (x.uniqueVisitors || 0),
          leads: t.leads + x.leads,
          callback: t.callback + x.callback,
          abandoned: t.abandoned + x.abandoned,
          paid: t.paid + x.paid,
        }),
        { uniqueVisitors: 0, leads: 0, callback: 0, abandoned: 0, paid: 0 }
      ),
    [grouped]
  );

  // A single page's tiles are re-derived from the CURRENT stats rather than the
  // row snapshot captured at click time, so they follow the Sales/Marketing
  // channel toggle (INT-56) — and a manual refresh — while drilled in. Same
  // per-slug summation as the list row above. No matching row means the page
  // has no traffic in this channel: honest zeros, never the other channel's
  // numbers. (Group drill-ins already read live `totals`.)
  const selectedLive = useMemo(() => {
    if (!selected || selected.isGroup) return null;
    const keys = new Set(
      (selected.slugs || []).map((s) => `${s.region}|${s.version}`)
    );
    return stats
      .filter((r) => keys.has(`${r.region}|${r.version}`))
      .reduce(
        (t, r) => ({
          uniqueVisitors: t.uniqueVisitors + (r.uniqueVisitors || 0),
          leads: t.leads + (r.leads || 0),
          callback: t.callback + (r.callback || 0),
          abandoned: t.abandoned + (r.abandoned || 0),
          paid: t.paid + (r.paid || 0),
        }),
        { uniqueVisitors: 0, leads: 0, callback: 0, abandoned: 0, paid: 0 }
      );
  }, [selected, stats]);

  useEffect(() => {
    if (!selected) return;
    setLeadsLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(PAGE_SIZE),
    });
    // A group (or any page whose slugs span regions) can't be expressed by a
    // single scalar region, so send the exact (region, version) pairs and let
    // the server $or them. Single-region drills keep the original params —
    // unchanged behaviour for every existing page.
    const regionSet = new Set(selected.slugs.map((s) => s.region));
    if (selected.isGroup || regionSet.size > 1) {
      params.set(
        "pairs",
        JSON.stringify(
          selected.slugs.map((s) => ({ region: s.region, version: s.version }))
        )
      );
    } else {
      params.set("region", selected.slugs[0].region);
      params.set("versions", selected.slugs.map((s) => s.version).join(","));
    }
    // from/to, not from/to: a focused day must narrow the PEOPLE list
    // too, otherwise the header says "Showing 07-08-2026" while the rows
    // below still cover the whole range.
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (stage) params.set("stage", stage);
    if (utmCampaign) params.set("utmCampaign", utmCampaign);
    params.set("channel", channel);
    axios
      .get(`${URL}/superadmin/checkout-analytics-leads?${params}`)
      .then((r) => setLeads(r.data))
      .catch(() => setLeads({ leads: [], total: 0 }))
      .finally(() => setLeadsLoading(false));
  }, [selected, stage, page, from, to, utmCampaign, refreshKey, channel]);

  const filtersActive = Boolean(
    source || subSourceF || slugF || search || utmCampaign || from || to
  );

  // The hourly section follows the same scope as the rest of the screen:
  // channel / source / UTM go to the server, page narrowing (a drill-in, a
  // group, or the list's sub-source / slug / search) is applied client-side.
  const hourlySource = (
    (SOURCES.find((x) => x.value === source) || {}).members || []
  ).join(",");
  // The hourly card ignores the list's date range, so it can't take its pages
  // from `stats`: those drop any page with no activity in the range, and are
  // [] while loading or after a failure, which would read as real zeros. A
  // saved group already knows its pages; the text filters match against every
  // page, fetched without dates.
  const hourlyNarrowed = Boolean(subSourceF || slugF || search.trim());
  const [hourlyPages, setHourlyPages] = useState({ rows: null, failed: false });
  useEffect(() => {
    if (!hourlyNarrowed) return undefined;
    let live = true;
    setHourlyPages({ rows: null, failed: false });
    const params = new URLSearchParams({ channel });
    if (hourlySource) params.set("source", hourlySource);
    if (utmCampaign) params.set("utmCampaign", utmCampaign);
    axios
      .get(`${URL}/superadmin/checkout-analytics-pages?${params}`)
      .then((r) => {
        if (live) setHourlyPages({ rows: r.data.data || [], failed: false });
      })
      .catch(() => live && setHourlyPages({ rows: null, failed: true }));
    return () => {
      live = false;
    };
  }, [hourlyNarrowed, hourlySource, utmCampaign, channel, refreshKey]);

  const hourlyListSlugs = useMemo(() => {
    if (!hourlyNarrowed) {
      return activeGroup ? asSlugs(activeGroup.pages) : null;
    }
    if (!hourlyPages.rows) return [];
    // Same rules as `grouped`: sub-source and slug match as an OR, and a search
    // hit on one version keeps every version of that page.
    const scoped = hourlyPages.rows.filter(
      (r) =>
        (!groupKeySet || groupKeySet.has(`${r.region}|${r.version}`)) &&
        ((!subSourceF && !slugF) ||
          (subSourceF && nameOf(r) === subSourceF) ||
          (slugF && slugOf(r) === slugF))
    );
    const q = search.trim().toLowerCase();
    const hitNames = new Set(
      scoped
        .filter(
          (r) =>
            !q ||
            nameOf(r).toLowerCase().includes(q) ||
            slugOf(r).includes(q)
        )
        .map(nameOf)
    );
    return asSlugs(scoped.filter((r) => hitNames.has(nameOf(r))));
  }, [
    hourlyNarrowed,
    hourlyPages,
    activeGroup,
    groupKeySet,
    subSourceF,
    slugF,
    search,
  ]);
  const resetFilters = () => {
    setDates({ startDate: null, endDate: null });
    setSource("");
    setSubSourceF(null);
    setSlugF(null);
    setSearch("");
    setUtmCampaignInput("");
    setUtmCampaign("");
    setSelected(null);
  };

  // --- Group actions ---
  const asGroupSelected = (g) => ({
    isGroup: true,
    name: g.name,
    source: null,
    slugs: (g.pages || []).map((p) => ({ region: p.region, version: p.version })),
  });
  // Combined = drill the whole group as one page; By page = the scoped table.
  const enterCombined = () => {
    setGroupMode("combined");
    setCampaignTab("leads");
    setStage("");
    setPage(0);
    setSelected(groupSelected);
  };
  const enterByPage = () => {
    setGroupMode("bypage");
    setSelected(null);
  };
  const openGroup = (g) => {
    setGroupEditor(null);
    setActiveGroupId(g.id);
    if (groupMode === "combined") {
      setCampaignTab("leads");
      setStage("");
      setPage(0);
      setSelected(asGroupSelected(g));
    } else {
      setSelected(null);
    }
  };
  const clearGroup = () => {
    setActiveGroupId(null);
    setSelected(null);
  };
  const newGroup = () => {
    setSelected(null);
    setPickerSearch("");
    setGroupEditor({ name: "", keys: new Set() });
  };
  const editGroup = (g) => {
    setSelected(null);
    setPickerSearch("");
    setGroupEditor({
      id: g.id,
      name: g.name,
      keys: new Set((g.pages || []).map((p) => `${p.region}|${p.version}`)),
    });
  };
  const deleteGroup = async (g) => {
    // Destructive + no undo — confirm before removing (superadmin convention).
    if (!window.confirm(`Delete filter group "${g.name}"?`)) return;
    try {
      await axios.delete(
        `${URL}/superadmin/checkout-analytics-groups/${encodeURIComponent(g.id)}`
      );
      setGroups((gs) => gs.filter((x) => x.id !== g.id));
      setGroupEditor(null);
      if (activeGroupId === g.id) {
        setActiveGroupId(null);
        setSelected(null);
      }
    } catch (e) {}
  };
  const saveGroup = async () => {
    if (!groupEditor) return;
    const name = (groupEditor.name || "").trim();
    if (!name || !groupEditor.keys.size) return;
    setSavingGroup(true);
    const pages = [...groupEditor.keys].map((k) => {
      const [region, version] = k.split("|");
      return { region, version };
    });
    try {
      const r = await axios.post(
        `${URL}/superadmin/checkout-analytics-groups`,
        { id: groupEditor.id, name, pages }
      );
      const saved = (r.data && r.data.group) || { id: groupEditor.id, name, pages };
      const list = await axios.get(
        `${URL}/superadmin/checkout-analytics-groups`
      );
      setGroups(list.data.groups || []);
      setGroupEditor(null);
      setActiveGroupId(saved.id);
      setGroupMode("combined");
      setCampaignTab("leads");
      setStage("");
      setPage(0);
      setSelected(asGroupSelected(saved));
    } catch (e) {
      // Leave the editor open so the picks aren't lost on a transient failure.
    } finally {
      setSavingGroup(false);
    }
  };
  // A table row is a sub-source that may cover several slugs; ticking it
  // selects/clears all of them together.
  const rowKeys = (x) => x.slugs.map((s) => `${s.region}|${s.version}`);
  const rowChecked = (x) =>
    !!groupEditor && rowKeys(x).every((k) => groupEditor.keys.has(k));
  const toggleRow = (x) =>
    setGroupEditor((ed) => {
      if (!ed) return ed;
      const keys = new Set(ed.keys);
      const ks = rowKeys(x);
      const allIn = ks.every((k) => keys.has(k));
      ks.forEach((k) => (allIn ? keys.delete(k) : keys.add(k)));
      return { ...ed, keys };
    });

  // Inside a campaign the ribbon reports THAT campaign, not the whole set —
  // otherwise the headline numbers contradict the table beneath them.
  // A single page reports its own frozen numbers; the whole set — and a group,
  // which is just a scoped set — reports the live scoped totals.
  const view = selected && !selected.isGroup && selectedLive ? selectedLive : totals;
  const convPct = view.leads ? (view.paid * 100) / view.leads : 0;
  // Rows carry a raw source ("aisensy_regional_plans"); the filter carries a
  // bucket value ("offers"). Both resolve to the bucket's label.
  const srcLabel = (v) =>
    (
      SOURCES.find((s) => s.value === v || s.members.includes(v)) || {
        label: v || "—",
      }
    ).label;

  const pageCount = Math.max(1, Math.ceil((leads.total || 0) / PAGE_SIZE));
  const showFrom = leads.total ? page * PAGE_SIZE + 1 : 0;
  const showTo = Math.min((page + 1) * PAGE_SIZE, leads.total || 0);

  /* One range control, reused by the all-campaigns report and by a single
     campaign's analytics, so the two can never be looking at different
     windows. */
  // What the report is actually plotting: a campaign's own series once one is
  // open, the whole window otherwise.
  const seriesDays = selected ? selectedDays : dailyDays;

  const rangeBar = (
    <Box className={classes.rangeBar}>
      <DateRangeIcon style={{ fontSize: 17, color: MUTED }} />
      {[...ANALYTICS_RANGES, { key: "custom", label: "Custom range" }].map((r) => (
        <button
          key={r.key}
          type="button"
          className={`${classes.rangeChip} ${
            rangeKey === r.key ? classes.rangeChipOn : ""
          }`}
          onClick={() => {
            setRangeKey(r.key);
            if (r.key !== "custom") setADates({ startDate: "", endDate: "" });
          }}
        >
          {r.label}
        </button>
      ))}
      {isCustom && (
        <Box
          className={classes.sweep}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          {/* Native date inputs rather than the react-dates picker used in the
              filters row: that component needs the filter row's layout to
              render correctly and collapses inside this chip strip. These also
              speak YYYY-MM-DD natively, which is exactly what the API wants —
              no moment object, so no chance of a timezone shift. */}
          <input
            type="date"
            className={classes.dateInput}
            value={aDates.startDate || ""}
            max={aDates.endDate || istToday()}
            // Read the value BEFORE the updater runs: React 16 pools synthetic
            // events, so `e.target` is null by the time a functional setState
            // callback is invoked.
            onChange={(e) => {
              const v = e.target.value;
              setADates((d) => ({ ...d, startDate: v }));
            }}
          />
          <span style={{ color: MUTED, fontSize: 13 }}>→</span>
          <input
            type="date"
            className={classes.dateInput}
            value={aDates.endDate || ""}
            min={aDates.startDate || undefined}
            max={istToday()}
            onChange={(e) => {
              const v = e.target.value;
              setADates((d) => ({ ...d, endDate: v }));
            }}
          />
        </Box>
      )}
      <span
        style={{
          marginLeft: "auto",
          fontSize: 11.5,
          color: MUTED,
          fontFamily: MONO,
        }}
      >
        {customIncomplete
          ? "pick both dates"
          : dailyLoading
          ? "loading…"
          : // On all time, spell out the span: "all time" for one campaign
            // means the dates IT was live, which is worth stating outright.
            `${rangeLabel} · ${
              seriesDays.length
                ? `${day(seriesDays[seriesDays.length - 1].day)} → ${day(
                    seriesDays[0].day
                  )} · ${seriesDays.length} days`
                : "no data"
            } · IST`}
      </span>
    </Box>
  );

  const labelled = (label, node) => (
    <>
      <span className={classes.fieldLabel}>{label}</span>
      {node}
    </>
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.header}>
          <Box>
            <Typography className={classes.title}>
              Checkout Pages Analytics
            </Typography>
            <Typography className={classes.subtitle}>
              Leads and payments for every checkout page — pick a row to see the
              people behind the numbers.
            </Typography>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Sales/Marketing scope toggle (INT-56) — flips the WHOLE screen
                between the marketing funnel (default), the /sales/ mirror pages,
                or both. Every other control, view and tile is unchanged; only
                which universe of pages the numbers come from changes. */}
            <Box
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "#f1f3f5",
                border: `1px solid ${LINE}`,
                borderRadius: 999,
                padding: 3,
              }}
            >
              {[
                { key: "marketing", label: "Marketing" },
                { key: "sales", label: "Sales" },
                { key: "all", label: "All" },
              ].map((c) => {
                const on = channel === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setChannel(c.key)}
                    style={{
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      background: on ? "#04CF66" : "transparent",
                      color: on ? "#fff" : MUTED,
                      transition: "background .12s",
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </Box>
            <Tooltip title="Refresh">
              <IconButton
                className={classes.refreshBtn}
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={loading || leadsLoading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter groups — a clear, labelled control. Pick a group to scope the
            whole screen; the banner below shows the applied filter and how to
            leave it. Creating/editing happens in a dialog, not the table. */}
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            padding: "10px 13px",
            marginBottom: activeGroup ? 10 : 14,
          }}
        >
          <span
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: ".07em",
              color: MUTED,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <LayersIcon style={{ fontSize: 16, color: G }} /> Filter groups
          </span>
          {groups.length === 0 && (
            <span style={{ fontSize: 12.5, color: MUTED }}>
              Save a set of checkout pages to read them together.
            </span>
          )}
          {groups.map((g) => {
            const on = activeGroupId === g.id;
            return (
              <span
                key={g.id}
                onClick={() => (on ? clearGroup() : openGroup(g))}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: `1px solid ${on ? "#cfeedd" : LINE}`,
                  cursor: "pointer",
                  background: on ? G_SOFT : "#fff",
                  color: on ? "#0f7a3d" : BODY,
                  fontSize: 13,
                  fontWeight: on ? 700 : 500,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: on ? G : "#c2ccc6",
                  }}
                />
                {g.name}
                <span style={{ fontSize: 11, color: MUTED }}>
                  {(g.pages || []).length}
                </span>
              </span>
            );
          })}
          <button
            type="button"
            onClick={newGroup}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 999,
              border: "1.5px solid #bfe4cd",
              cursor: "pointer",
              background: G_SOFT,
              color: "#0f7a3d",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            ＋ New group
          </button>
        </Box>

        {/* Applied-filter banner: unmistakable state + a one-click way out. */}
        {activeGroup && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              background: `linear-gradient(180deg, ${G_SOFT}, #fff)`,
              border: "1.5px solid #cfeedd",
              borderRadius: 12,
              padding: "11px 14px",
              marginBottom: 14,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14,
                fontWeight: 700,
                color: "#0f7a3d",
              }}
            >
              <LayersIcon style={{ fontSize: 18 }} />
              Filtered to “{activeGroup.name}”
            </span>
            <span
              style={{
                fontSize: 12.5,
                color: BODY,
                background: "#fff",
                border: "1px solid #cfeedd",
                borderRadius: 999,
                padding: "3px 10px",
              }}
            >
              {(activeGroup.pages || []).length} pages
            </span>
            <span
              style={{
                display: "inline-flex",
                background: "#fff",
                border: "1px solid #cfeedd",
                borderRadius: 10,
                padding: 4,
                gap: 4,
              }}
            >
              {[
                { key: "combined", label: "Combined", onClick: enterCombined },
                { key: "bypage", label: "By page", onClick: enterByPage },
              ].map((t) => {
                const on = groupMode === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={t.onClick}
                    style={{
                      border: 0,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontWeight: 600,
                      fontSize: 13,
                      padding: "6px 15px",
                      borderRadius: 7,
                      background: on ? G : "transparent",
                      color: on ? "#fff" : MUTED,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </span>
            <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => editGroup(activeGroup)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 13,
                  fontWeight: 600,
                  color: BODY,
                  cursor: "pointer",
                  padding: "6px 11px",
                  borderRadius: 8,
                  border: `1px solid ${LINE}`,
                  background: "#fff",
                  fontFamily: "inherit",
                }}
              >
                <EditIcon style={{ fontSize: 15 }} /> Edit
              </button>
              <button
                type="button"
                onClick={clearGroup}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#b95000",
                  cursor: "pointer",
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #fbe3d3",
                  background: "#fff",
                  fontFamily: "inherit",
                }}
              >
                <CloseIcon style={{ fontSize: 15 }} /> Clear filter
              </button>
            </span>
          </Box>
        )}

        {/* Kept in BOTH modes: hiding it moved the card 96px up when switching
            to the report, so the mode button jumped. The report's own tiles sit
            directly under a range bar that names the window, so the two read as
            "campaign overall" and "campaign in this range" rather than as a
            contradiction. */}
        <Box className={classes.ribbon}>
          {[
            { icon: <VisibilityIcon style={{ fontSize: 22 }} />, s: STAGE.LEAD, v: fmt(view.uniqueVisitors), l: "Visits" },
            { icon: <PeopleAltIcon style={{ fontSize: 22 }} />, s: STAGE.LEAD, v: fmt(view.leads), l: "Leads" },
            { icon: <PhoneCallbackIcon style={{ fontSize: 22 }} />, s: STAGE.CALLBACK, v: fmt(view.callback), l: "Callback" },
            { icon: <RemoveShoppingCartIcon style={{ fontSize: 22 }} />, s: STAGE.ABANDONED, v: fmt(view.abandoned), l: "Abandoned" },
            { icon: <MonetizationOnIcon style={{ fontSize: 22 }} />, s: STAGE.PAID, v: fmt(view.paid), l: "Paid" },
          ].map((k) => (
            <Box key={k.l} className={classes.seg}>
              <Box className={classes.segIcon} style={{ backgroundColor: k.s.bg, color: k.s.fg }}>
                {k.icon}
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Typography className={classes.segValue}>{k.v}</Typography>
                <Typography className={classes.segLabel} style={{ color: k.s.fg }}>
                  {k.l}
                </Typography>
              </Box>
            </Box>
          ))}
          <Box className={`${classes.seg} ${classes.segOut}`}>
            <Box className={classes.segIcon} style={{ backgroundColor: G_SOFT, color: "#0f7a3d" }}>
              <TrendingUpIcon style={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography className={classes.segValue}>{convPct.toFixed(1)}%</Typography>
              <Typography className={classes.segLabel} style={{ color: "#0f7a3d" }}>
                Lead → paid
              </Typography>
            </Box>
          </Box>
        </Box>

        {!selected && (
          <Paper
            className={`${classes.card} ${classes.section}`}
            elevation={0}
            style={{ padding: "14px 18px" }}
          >
            <HourlyComparison
              slugs={hourlyListSlugs}
              slugsPending={
                hourlyNarrowed && !hourlyPages.rows && !hourlyPages.failed
              }
              slugsFailed={hourlyNarrowed && hourlyPages.failed}
              channel={channel}
              source={hourlySource}
              utmCampaign={utmCampaign}
              refreshKey={refreshKey}
              classes={classes}
            />
          </Paper>
        )}

        {!selected && (
        <Paper className={`${classes.card} ${classes.section}`} elevation={0}>
          <Typography component="div" className={classes.heading}>
            <FilterListIcon style={{ color: G }} /> Filters
          </Typography>

          <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
            <Grid item style={{ flex: "1.1 1 0", minWidth: 0 }}>
              {labelled(
                "Search",
                <TextField
                  fullWidth
                  className={classes.field}
                  placeholder="Campaign name or slug"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 17, color: MUTED }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Grid>

            <Grid item style={{ flex: "1.1 1 0", minWidth: 0 }}>
              {labelled(
                "UTM Campaign",
                <TextField
                  fullWidth
                  className={classes.field}
                  placeholder="Type & press Enter"
                  value={utmCampaignInput}
                  onChange={(e) => setUtmCampaignInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setUtmCampaign(utmCampaignInput.trim());
                      setSelected(null);
                    }
                  }}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: utmCampaign ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setUtmCampaignInput("");
                            setUtmCampaign("");
                          }}
                        >
                          <CloseIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
            </Grid>

            <Grid item style={{ flex: "0.9 1 0", minWidth: 0 }}>
              {labelled(
                "Source",
                <TextField
                  select
                  fullWidth
                  className={classes.field}
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                    setSelected(null);
                  }}
                  InputProps={{ disableUnderline: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  {SOURCES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>

            <Grid item style={{ flex: "1.7 1 0", minWidth: 0 }}>
              {labelled(
                "Sub-source",
                <Autocomplete
                  PopperComponent={WidePopper}
                  classes={{ paper: classes.optionPaper }}
                  options={subSourceOptions}
                  value={subSourceF}
                  onChange={(_, v) => {
                    setSubSourceF(v);
                    setSelected(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={classes.field}
                      placeholder="Any sub-source"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item style={{ flex: "1.7 1 0", minWidth: 0 }}>
              {labelled(
                "Slug",
                <Autocomplete
                  PopperComponent={WidePopper}
                  classes={{ paper: classes.optionPaper }}
                  options={slugOptions}
                  value={slugF}
                  onChange={(_, v) => {
                    setSlugF(v);
                    setSelected(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={classes.field}
                      placeholder="Any slug"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                    />
                  )}
                />
              )}
            </Grid>

            <Grid item style={{ flex: "0 0 auto" }}>
              {labelled(
                "Date range",
                <div className={classes.rangeWrap}>
                  <DateRangePicker
                    startDate={dates.startDate}
                    endDate={dates.endDate}
                    startDateId="checkoutAnalyticsFrom"
                    endDateId="checkoutAnalyticsTo"
                    startDatePlaceholderText="From"
                    endDatePlaceholderText="To"
                    onDatesChange={({ startDate, endDate }) => {
                      setDates({ startDate, endDate });
                      setSelected(null);
                    }}
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                    // Leads can't be created in the future, so offering future
                    // days would only ever produce an empty range.
                    isOutsideRange={(day) => moment().diff(day) < 0}
                    numberOfMonths={2}
                    // The picker sits at the right edge of the row, so a
                    // left-anchored calendar opens off-screen.
                    anchorDirection="right"
                    navPosition="navPositionTop"
                    hideKeyboardShortcutsPanel
                    customArrowIcon={<span className={classes.rangeArrow}>→</span>}
                    screenReaderInputMessage=" "
                    small
                    readOnly
                    minimumNights={0}
                  />
                </div>
              )}
            </Grid>

            <Grid item style={{ flex: "0 0 auto" }}>
              <span className={classes.fieldLabel}>&nbsp;</span>
              <Tooltip title="Reset filters">
                <span>
                  <IconButton
                    className={classes.resetIcon}
                    onClick={resetFilters}
                    disabled={!filtersActive}
                  >
                    <CloseIcon style={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>

          {filtersActive && (
            <Box className={classes.chipsRow}>
              {source && (
                <Chip
                  size="small"
                  className={classes.activeChip}
                  label={`Source: ${srcLabel(source)}`}
                  onDelete={() => setSource("")}
                />
              )}
              {subSourceF && (
                <Chip
                  size="small"
                  className={classes.activeChip}
                  label={`Sub-source: ${subSourceF}`}
                  onDelete={() => setSubSourceF(null)}
                />
              )}
              {slugF && (
                <Chip
                  size="small"
                  className={classes.activeChip}
                  label={`Slug: ${slugF}`}
                  onDelete={() => setSlugF(null)}
                />
              )}
              {search && (
                <Chip
                  size="small"
                  className={classes.activeChip}
                  label={`Search: ${search}`}
                  onDelete={() => setSearch("")}
                />
              )}
            </Box>
          )}
        </Paper>
        )}

        {/* The synthetic group has no "list" to go back to — the Combined /
            By page toggle is its navigation — so the back link shows only for
            a real single-page drill, returning to the scoped group list when
            one is active. */}
        {selected && !selected.isGroup && (
          <span className={classes.back} onClick={() => setSelected(null)}>
            <ArrowBackIcon style={{ fontSize: 15 }} />{" "}
            {activeGroup ? `Back to ${activeGroup.name}` : "All campaigns"}
          </span>
        )}

        <Paper className={classes.card} elevation={0}>
          <Box className={classes.resultsHeader}>
            <Typography
              component="div"
              className={classes.heading}
              style={{ marginBottom: 0 }}
            >
              <ViewListIcon style={{ color: G }} />
              {selected ? selected.name : "Campaigns"}
              <span className={classes.countBadge}>
                {fmt(
                  selected
                    ? campaignTab === "analytics"
                      ? sumMetrics(selectedDays).leads
                      : leads.total || 0
                    : grouped.length
                )}
              </span>
            </Typography>

            {!selected ? (
              <Typography variant="body2" style={{ color: MUTED }}>
                Sorted by{" "}
                {(COLUMNS.find((c) => c.key === sortBy) || {}).label}
                {sortDir === "desc" ? " ↓" : " ↑"}
              </Typography>
            ) : selected.isGroup ? null : (
              <Box className={classes.pager}>
                {/* Stage filter belongs to the lead list only. */}
                <Box
                  className={classes.stagePills}
                  style={campaignTab === "analytics" ? { display: "none" } : undefined}
                >
                  {STAGE_OPTIONS.map((o) => {
                    const on = stage === o.value;
                    const tone = o.value ? STAGE[o.value] : null;
                    return (
                      <button
                        key={o.value || "all"}
                        type="button"
                        className={classes.stagePill}
                        onClick={() => {
                          setStage(o.value);
                          setPage(0);
                        }}
                        style={
                          on
                            ? {
                                backgroundColor: tone ? tone.bg : G_SOFT,
                                color: tone ? tone.fg : "#0f7a3d",
                                borderColor: tone ? tone.fg + "55" : "#cfeedd",
                              }
                            : undefined
                        }
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </Box>
                {/* Paging belongs to the lead list; the Analytics tab is not
                    paginated. */}
                {campaignTab !== "analytics" && (
                  <>
                    <Typography variant="body2" style={{ color: MUTED }}>
                      {showFrom}-{showTo} of {fmt(leads.total || 0)}
                    </Typography>
                    <IconButton
                      className={classes.pagerBtn}
                      size="small"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <NavigateBeforeIcon fontSize="small" />
                    </IconButton>
                    <span className={classes.pagePill}>{page + 1}</span>
                    <IconButton
                      className={classes.pagerBtn}
                      size="small"
                      disabled={page + 1 >= pageCount}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <NavigateNextIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
                {/* Always the last child of this row, so the control sits in
                    exactly the same place whether the stage pills and pager
                    are showing or not. */}
                {!selected.isGroup && (
                  <button
                    type="button"
                    className={`${classes.modeBtn} ${
                      campaignTab === "analytics" ? classes.modeBtnBack : ""
                    }`}
                    onClick={() =>
                      setCampaignTab(
                        campaignTab === "analytics" ? "leads" : "analytics"
                      )
                    }
                  >
                    <span key={campaignTab} className={classes.modeBtnInner}>
                      {campaignTab === "analytics" ? (
                        <>
                          <PeopleAltIcon className={classes.modeIcon} />
                          Back to leads
                        </>
                      ) : (
                        <>
                          <InsightsIcon className={classes.modeIcon} />
                          Switch to analytics view
                        </>
                      )}
                    </span>
                  </button>
                )}
              </Box>
            )}
          </Box>

          {!selected ? (
            loading ? (
              <Box className={classes.empty}>
                <CircularProgress size={26} style={{ color: G }} />
              </Box>
            ) : !grouped.length ? (
              <Box className={classes.empty}>
                <InboxIcon style={{ fontSize: 40, color: "#c8d4ce" }} />
                <Typography className={classes.emptyTitle}>
                  No leads in this range
                </Typography>
                <Typography className={classes.emptyBody}>
                  Widen the dates, or reset the filters to see every page.
                </Typography>
              </Box>
            ) : (
              <TableContainer className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((c) => (
                        <TableCell
                          key={c.key}
                          className={classes.headCell}
                          align={c.align}
                          sortDirection={sortBy === c.key ? sortDir : false}
                        >
                          <TableSortLabel
                            className={classes.sortLabel}
                            active={sortBy === c.key}
                            direction={
                              sortBy === c.key
                                ? sortDir
                                : c.numeric
                                ? "desc"
                                : "asc"
                            }
                            onClick={() => handleSort(c)}
                          >
                            {c.label}
                          </TableSortLabel>
                          {c.key === "uniqueVisitors" && (
                            <Tooltip
                              title={`Unique visitors, recorded from ${VISITS_SINCE}. Leads from before that date have no visit data.`}
                            >
                              <InfoOutlinedIcon className={classes.headInfo} />
                            </Tooltip>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped.map((x) => {
                      const cv = x.leads ? (x.paid * 100) / x.leads : 0;
                      return (
                        <TableRow
                          key={x.name}
                          className={classes.clickRow}
                          onClick={() => {
                            setSelected(x);
                            setStage("");
                            setPage(0);
                            setCampaignTab("leads");
                          }}
                        >
                          <TableCell className={classes.cell}>
                            <div className={classes.srcTag}>
                              {srcLabel(x.source)}
                            </div>
                            <div className={classes.nameText}>{x.name}</div>
                            <div className={classes.slugText}>
                              {x.slugs
                                .map((s) => `${s.region}/${s.version}`)
                                .join(" · ")}
                            </div>
                          </TableCell>
                          <TableCell className={`${classes.cell} ${classes.num}`}>
                            {/* Summed across the slugs merged into this row,
                                so it over-counts a visitor who saw two
                                variants. Exact for single-slug rows. */}
                            {/* Dash, not 0. Every page predates visit
                                tracking, so a hard 0 beside thousands of
                                leads reads as broken data rather than
                                "not measured yet". */}
                            {!x.uniqueVisitors ? (
                              <span style={{ color: MUTED }}>—</span>
                            ) : x.slugs.length > 1 ? (
                              <Tooltip
                                title={`Approximate: summed across ${x.slugs.length} slugs, so a visitor who saw more than one is counted twice`}
                              >
                                <span>~{fmt(x.uniqueVisitors)}</span>
                              </Tooltip>
                            ) : (
                              fmt(x.uniqueVisitors)
                            )}
                          </TableCell>
                          <TableCell className={`${classes.cell} ${classes.num}`}>
                            {fmt(x.leads)}
                          </TableCell>
                          <TableCell
                            className={`${classes.cell} ${classes.num}`}
                            style={{
                              color: x.callback ? STAGE.CALLBACK.fg : MUTED,
                            }}
                          >
                            {fmt(x.callback)}
                          </TableCell>
                          <TableCell
                            className={`${classes.cell} ${classes.num}`}
                            style={{
                              color: x.abandoned ? STAGE.ABANDONED.fg : MUTED,
                            }}
                          >
                            {fmt(x.abandoned)}
                          </TableCell>
                          <TableCell
                            className={`${classes.cell} ${classes.num}`}
                            style={{
                              color: x.paid ? STAGE.PAID.fg : MUTED,
                              fontWeight: x.paid ? 700 : 400,
                            }}
                          >
                            {fmt(x.paid)}
                          </TableCell>
                          <TableCell className={`${classes.cell} ${classes.num}`}>
                            <span className={classes.convCell}>
                              <span className={classes.convTrack}>
                                <span
                                  className={classes.convFill}
                                  style={{
                                    width: `${Math.min(cv * 2.2, 100)}%`,
                                  }}
                                />
                              </span>
                              <span
                                className={classes.convPct}
                                style={{
                                  color:
                                    cv >= 20
                                      ? "#0f7a3d"
                                      : cv < 5
                                        ? "#b95000"
                                        : BODY,
                                }}
                              >
                                {cv.toFixed(1)}%
                              </span>
                            </span>
                          </TableCell>
                          <TableCell
                            className={`${classes.cell} ${classes.num} ${classes.mut}`}
                          >
                            {day(x.lastLeadAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <>
              {/* A group's Combined view stacks analytics AND leads; a single
                  page shows one tab at a time. */}
              {(selected.isGroup || campaignTab === "analytics") && (
                <>
                  <Paper
                    className={`${classes.card} ${classes.pop}`}
                    elevation={0}
                    style={{ margin: "0 16px 16px", padding: "14px 18px" }}
                  >
                    <HourlyComparison
                      slugs={selected.slugs}
                      channel={channel}
                      source={hourlySource}
                      utmCampaign={utmCampaign}
                      refreshKey={refreshKey}
                      classes={classes}
                    />
                  </Paper>
                  <Box className={classes.pop}>{rangeBar}</Box>
              {customIncomplete ? (
                <Box className={classes.empty}>
                  <DateRangeIcon style={{ fontSize: 38, color: "#c8d4ce" }} />
                  <Typography className={classes.emptyTitle}>
                    Pick a date range
                  </Typography>
                  <Typography className={classes.emptyBody}>
                    Choose a start and an end date above to build the report.
                  </Typography>
                </Box>
              ) : dailyLoading ? (
                <Box className={classes.empty}>
                  <CircularProgress size={26} style={{ color: G }} />
                </Box>
              ) : (
                <Box style={{ padding: "4px 16px 16px" }}>
                  <CampaignTrend
                    days={selectedDays}
                    name={selected.name}
                    classes={classes}
                    hideTiles={selected.isGroup}
                  />

                  <Box
                    className={classes.pop}
                    style={{ marginTop: 22, animationDelay: "260ms" }}
                  >
                    <Typography
                      component="div"
                      className={classes.heading}
                      style={{ marginBottom: 2, fontSize: 14 }}
                    >
                      <GridOnIcon style={{ color: G, fontSize: 18 }} />
                      Monthly calendar
                    </Typography>
                    <Typography
                      component="div"
                      className={classes.subtitle}
                      style={{ marginBottom: 12 }}
                    >
                      Every day of the month for this campaign, shaded against
                      the month's best day. Hover a date for its full numbers.
                    </Typography>
                    <MonthCalendar days={selectedDays} istTodayKey={istToday()} />
                  </Box>

                  <Box
                    className={classes.pop}
                    style={{ marginTop: 20, animationDelay: "340ms" }}
                  >
                    <Typography
                      component="div"
                      className={classes.heading}
                      style={{ marginBottom: 2, fontSize: 14 }}
                    >
                      <TrendingUpIcon style={{ color: G, fontSize: 18 }} />
                      Day by day
                    </Typography>
                    <Typography
                      component="div"
                      className={classes.subtitle}
                      style={{ marginBottom: 10 }}
                    >
                      IST days. Each figure carries its change against the
                      previous calendar day.
                    </Typography>
                    <DayWiseTable
                      days={selectedDays}
                      classes={classes}
                      openDay={null}
                      setOpenDay={() => {}}
                    />
                  </Box>
                </Box>
              )}
                </>
              )}
              {selected.isGroup && (
                <Box className={classes.pop} style={{ padding: "20px 16px 2px" }}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      component="div"
                      className={classes.heading}
                      style={{ marginBottom: 0, fontSize: 14 }}
                    >
                      <PeopleAltIcon style={{ color: G, fontSize: 18 }} />
                      Combined leads
                      <span className={classes.countBadge}>
                        {fmt(leads.total || 0)}
                      </span>
                    </Typography>
                    {/* The stage filter + pager sit WITH the combined leads
                        table, not up in the page header where they'd be detached
                        from the rows they act on. */}
                    <Box className={classes.pager}>
                      <Box className={classes.stagePills}>
                        {STAGE_OPTIONS.map((o) => {
                          const on = stage === o.value;
                          const tone = o.value ? STAGE[o.value] : null;
                          return (
                            <button
                              key={o.value || "all"}
                              type="button"
                              className={classes.stagePill}
                              onClick={() => {
                                setStage(o.value);
                                setPage(0);
                              }}
                              style={
                                on
                                  ? {
                                      backgroundColor: tone ? tone.bg : G_SOFT,
                                      color: tone ? tone.fg : "#0f7a3d",
                                      borderColor: tone
                                        ? tone.fg + "55"
                                        : "#cfeedd",
                                    }
                                  : undefined
                              }
                            >
                              {o.label}
                            </button>
                          );
                        })}
                      </Box>
                      <Typography variant="body2" style={{ color: MUTED }}>
                        {showFrom}-{showTo} of {fmt(leads.total || 0)}
                      </Typography>
                      <IconButton
                        className={classes.pagerBtn}
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                      >
                        <NavigateBeforeIcon fontSize="small" />
                      </IconButton>
                      <span className={classes.pagePill}>{page + 1}</span>
                      <IconButton
                        className={classes.pagerBtn}
                        size="small"
                        disabled={page + 1 >= pageCount}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        <NavigateNextIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    component="div"
                    className={classes.subtitle}
                    style={{ marginTop: 4, marginBottom: 4 }}
                  >
                    Every lead across the {selected.slugs.length} pages in this
                    group.
                  </Typography>
                </Box>
              )}
              {(selected.isGroup || campaignTab !== "analytics") && (
                <>
                  {leadsLoading ? (
            <Box className={classes.empty}>
              <CircularProgress size={26} style={{ color: G }} />
            </Box>
          ) : !leads.leads.length ? (
            <Box className={classes.empty}>
              <InboxIcon style={{ fontSize: 40, color: "#c8d4ce" }} />
              <Typography className={classes.emptyTitle}>
                Nothing at this stage
              </Typography>
              <Typography className={classes.emptyBody}>
                Choose a different stage, or widen the date range.
              </Typography>
            </Box>
          ) : (
            <TableContainer className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Date",
                      "Name",
                      "Company",
                      "Phone",
                      "Email",
                      "UTM Source",
                      "UTM Medium",
                      "UTM Campaign",
                      "Stage",
                      "Payment ID",
                    ].map((h) => (
                      <TableCell key={h} className={classes.headCell}>
                        {h}
                      </TableCell>
                    ))}
                    <TableCell className={classes.headCell} align="right">
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.leads.map((l, i) => {
                    const st = STAGE[l.stage] || STAGE.LEAD;
                    return (
                      <TableRow key={i} hover>
                        <TableCell
                          className={`${classes.cell} ${classes.mut}`}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {day(l.createdAt)}
                        </TableCell>
                        <TableCell
                          className={classes.cell}
                          style={{ fontWeight: 600, color: INK }}
                        >
                          {l.fullName || "—"}
                        </TableCell>
                        <TableCell className={classes.cell}>
                          {l.organizationName || "—"}
                        </TableCell>
                        <TableCell
                          className={`${classes.cell} ${classes.mut}`}
                          style={{
                            whiteSpace: "nowrap",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {l.phone || "—"}
                        </TableCell>
                        <TableCell className={`${classes.cell} ${classes.mut}`}>
                          {l.email || "—"}
                        </TableCell>
                        <TableCell className={`${classes.cell} ${classes.mut}`}>
                          {l.utmSource || "—"}
                        </TableCell>
                        <TableCell className={`${classes.cell} ${classes.mut}`}>
                          {l.utmMedium || "—"}
                        </TableCell>
                        <TableCell className={`${classes.cell} ${classes.mut}`}>
                          {l.utmCampaign || "—"}
                        </TableCell>
                        <TableCell className={classes.cell}>
                          <Chip
                            size="small"
                            label={st.label}
                            style={{
                              backgroundColor: st.bg,
                              color: st.fg,
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          />
                        </TableCell>
                        <TableCell className={classes.cell}>
                          <Tooltip title={l.paymentId || ""} placement="top">
                            <span className={classes.mono}>
                              {l.paymentId || "—"}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          className={`${classes.cell} ${classes.num}`}
                          // Bold ink means money actually taken. A non-paid row
                          // still carries a plan amount; keep it muted so a
                          // cart value never reads as revenue.
                          style={{
                            fontWeight: l.stage === "PAID" ? 700 : 400,
                            color: l.stage === "PAID" ? INK : MUTED,
                          }}
                        >
                          {money(l.amount, l.currency)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* New / Edit group dialog: name + a searchable checklist of pages. */}
      {groupEditor && (
        <Box
          onClick={() => setGroupEditor(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(14,31,24,.45)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(560px, 100%)",
              maxHeight: "85vh",
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 20px 60px rgba(0,0,0,.3)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              style={{
                padding: "16px 18px",
                borderBottom: `1px solid ${LINE}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: INK,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <LayersIcon style={{ fontSize: 19, color: G }} />
                {groupEditor.id ? "Edit filter group" : "New filter group"}
              </span>
              <CloseIcon
                style={{ cursor: "pointer", color: MUTED }}
                onClick={() => setGroupEditor(null)}
              />
            </Box>

            <Box style={{ padding: "16px 18px", overflow: "auto" }}>
              <TextField
                fullWidth
                placeholder="Group name  (e.g. Q4 Growth Push)"
                value={groupEditor.name}
                onChange={(e) => {
                  const v = e.target.value;
                  setGroupEditor((ed) => ({ ...ed, name: v }));
                }}
                InputProps={{ disableUnderline: true }}
                className={classes.field}
                style={{ background: "#f0f3f0", borderRadius: 9, marginBottom: 12 }}
              />
              <TextField
                fullWidth
                placeholder="Search pages by name or slug…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 17, color: MUTED }} />
                    </InputAdornment>
                  ),
                }}
                className={classes.field}
                style={{ background: "#f0f3f0", borderRadius: 9, marginBottom: 10 }}
              />
              <Box
                style={{
                  border: `1px solid ${LINE}`,
                  borderRadius: 10,
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {pickerPages
                  .filter((p) => {
                    const q = pickerSearch.trim().toLowerCase();
                    return (
                      !q ||
                      p.name.toLowerCase().includes(q) ||
                      p.slugs.some((s) =>
                        `${s.region}/${s.version}`.includes(q)
                      )
                    );
                  })
                  .map((p) => {
                    const checked = rowChecked(p);
                    return (
                      <Box
                        key={p.name}
                        onClick={() => toggleRow(p)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 11,
                          padding: "9px 12px",
                          borderBottom: `1px solid ${LINE}`,
                          cursor: "pointer",
                          background: checked ? G_SOFT : "#fff",
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          size="small"
                          style={{ color: G, padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(p);
                          }}
                        />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 600, color: INK }}>
                            {p.name}
                          </span>
                          <div
                            style={{
                              fontSize: 11,
                              color: MUTED,
                              fontFamily: MONO,
                            }}
                          >
                            {p.slugs
                              .map((s) => `${s.region}/${s.version}`)
                              .join(" · ")}
                          </div>
                        </Box>
                        <span style={{ fontSize: 12, color: MUTED }}>
                          {fmt(p.leads)} leads
                        </span>
                      </Box>
                    );
                  })}
                {!pickerPages.length && (
                  <Box style={{ padding: 16, color: MUTED, fontSize: 13 }}>
                    Loading pages…
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              style={{
                padding: "14px 18px",
                borderTop: `1px solid ${LINE}`,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 13, color: BODY }}>
                <b style={{ color: INK }}>{groupEditor.keys.size}</b> page
                {groupEditor.keys.size === 1 ? "" : "s"} selected
              </span>
              {groupEditor.id && (
                <button
                  type="button"
                  onClick={() =>
                    deleteGroup({ id: groupEditor.id, name: groupEditor.name })
                  }
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "#b95000",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Delete group
                </button>
              )}
              <span style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setGroupEditor(null)}
                  style={{
                    border: 0,
                    background: "#f0f3f0",
                    color: BODY,
                    fontWeight: 700,
                    fontSize: 13.5,
                    padding: "9px 16px",
                    borderRadius: 9,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveGroup}
                  disabled={
                    savingGroup ||
                    !groupEditor.name.trim() ||
                    !groupEditor.keys.size
                  }
                  style={{
                    border: 0,
                    background:
                      savingGroup ||
                      !groupEditor.name.trim() ||
                      !groupEditor.keys.size
                        ? "#a9d9bd"
                        : G,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13.5,
                    padding: "9px 18px",
                    borderRadius: 9,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {savingGroup
                    ? "Saving…"
                    : groupEditor.id
                    ? "Save changes"
                    : "Create group"}
                </button>
              </span>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
